import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Home from './Home';
import MarketSnapshot from './MarketSnapshot';
import { exchanges } from './ExchangeSelector';
import { Reveal, Rise, RiseWords, AnimatedNumber, useInView, usePrefersReducedMotion } from './motion';
import {
  Container,
  Card,
  Eyebrow,
  DisplayTitle,
  SectionTitle,
  CardTitle,
  Lead,
  SectionHeader,
  Button,
  TextLink,
  IconChevronRight,
  IconLayers,
  IconCalendar,
  IconActivity,
  IconBolt,
} from './ui';

const activeExchanges = exchanges.filter((exchange) => exchange.status === 'active');
const featureIcons = [IconLayers, IconCalendar, IconActivity, IconBolt];

// ------------------------------------------------------------------ hero

const HeroSection = styled.section`
  position: relative;
  padding: clamp(56px, 10vw, 128px) 0 0;
  background: radial-gradient(70% 60% at 50% 0%, var(--hero-glow), transparent 72%);
  overflow: hidden;
  isolation: isolate;
`;

// A soft light that trails the pointer. A CSS transition on transform re-targets from the
// current position, so fast movement glides instead of jumping.
const PointerGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 720px;
  height: 720px;
  margin: -360px 0 0 -360px;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--hero-glow), transparent);
  opacity: 0;
  pointer-events: none;
  transition:
    transform 1.4s var(--ease-out),
    opacity 600ms ease;

  @media (hover: none), (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const HeroInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 880px;
  margin: 0 auto;

  ${Lead} {
    margin-top: 22px;
    max-width: 36em;
  }
`;

const LiveChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  padding: 6px 12px 6px 10px;
  border-radius: 980px;
  background: var(--surface);
  border: 1px solid var(--separator);
  box-shadow: var(--shadow-sm);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
`;

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(2.6); opacity: 0; }
`;

// The one deliberate loop on the page: a slow "live" ping, small enough not to distract.
const LiveDot = styled.span`
  position: relative;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--positive);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: var(--positive);
    animation: ${ping} 2.4s var(--ease-out) infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const HeroActions = styled(Rise)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px 24px;
  margin-top: 34px;
`;

const draw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PulseFigure = styled.div`
  margin: clamp(40px, 6vw, 72px) calc(var(--gutter) * -1) 0;
  color: var(--accent);

  svg {
    display: block;
    width: 100%;
    height: clamp(90px, 14vw, 150px);
    overflow: visible;
  }

  .line {
    stroke-dasharray: 1;
    stroke-dashoffset: 0;
    animation: ${draw} 2.2s var(--ease-out) 0.5s both;
  }

  .area {
    animation: ${fadeIn} 1.2s ease 1.4s both;
  }

  @media (prefers-reduced-motion: reduce) {
    .line,
    .area {
      animation: none;
    }
  }
`;

const PULSE_PATH =
  'M0 96 C60 96 92 70 142 72 S232 108 292 102 S382 50 442 54 S542 100 602 94 S692 36 762 42 S862 104 922 98 S1012 58 1072 62 S1152 80 1188 76';

// Decorative: a rate oscillating around the zero line — the product in one gesture.
function PulseGraphic() {
  return (
    <PulseFigure aria-hidden="true">
      <svg viewBox="0 0 1200 160" preserveAspectRatio="none" fill="none">
        <defs>
          <linearGradient id="pulse-stroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="currentColor" stopOpacity="0" />
            <stop offset="0.25" stopColor="currentColor" stopOpacity="0.55" />
            <stop offset="1" stopColor="currentColor" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="pulse-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.14" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1="84" x2="1200" y2="84" stroke="var(--separator-strong)" strokeDasharray="3 6" vectorEffect="non-scaling-stroke" />
        <path className="area" d={`${PULSE_PATH} L1188 160 L0 160 Z`} fill="url(#pulse-area)" />
        <path
          className="line"
          pathLength="1"
          d={PULSE_PATH}
          stroke="url(#pulse-stroke)"
          strokeWidth="2.25"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </PulseFigure>
  );
}

const StatsRow = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid var(--separator);

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled(Reveal)`
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  padding: 28px 24px 32px 0;

  & + & {
    padding-left: 24px;
    border-left: 1px solid var(--separator);
  }

  @media (max-width: 860px) {
    &:nth-child(odd) {
      padding-left: 0;
      border-left: 0;
    }

    &:nth-child(n + 3) {
      border-top: 1px solid var(--separator);
    }
  }

  dt {
    font-size: 0.875rem;
    line-height: 1.4;
    color: var(--text-secondary);
    margin-top: 6px;
  }

  dd {
    font-family: var(--font-display);
    font-size: clamp(1.75rem, 3.4vw, 2.5rem);
    font-weight: 650;
    letter-spacing: -0.03em;
    line-height: 1.1;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }
`;

function HeroStats({ stats }) {
  const [ref, inView] = useInView();
  return (
    <StatsRow ref={ref}>
      {stats.map((stat, index) => {
        const value = stat.value.replace('{exchanges}', activeExchanges.length);
        const numeric = /^\d+$/.test(value);
        return (
          <Stat key={stat.label} delay={index * 90}>
            <dt>{stat.label}</dt>
            <dd>{numeric ? <AnimatedNumber value={Number(value)} start={inView} duration={1400} /> : value}</dd>
          </Stat>
        );
      })}
    </StatsRow>
  );
}

// ------------------------------------------------------------------ coverage ticker

const marquee = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
`;

const CoverageSection = styled.section`
  padding: clamp(28px, 4vw, 40px) 0;
  border-top: 1px solid var(--separator);
  border-bottom: 1px solid var(--separator);
  background: var(--surface-muted);
`;

const CoverageLabel = styled.p`
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-tertiary);
  margin-bottom: 18px;
`;

const Ticker = styled.div`
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
`;

// Two identical copies side by side; sliding by exactly half loops seamlessly.
const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${marquee} 48s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  ul {
    display: flex;
    list-style: none;
  }

  li {
    padding-right: 48px;
    font-size: 1.1875rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  @media (prefers-reduced-motion: reduce) {
    width: auto;
    animation: none;
    justify-content: center;

    ul {
      flex-wrap: wrap;
      justify-content: center;
      gap: 10px 0;
    }

    ul + ul {
      display: none;
    }
  }
`;

// ------------------------------------------------------------------ features

const Section = styled.section`
  padding-top: clamp(64px, 9vw, 112px);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

// Hover lifts the card and a soft light follows the pointer across it.
const FeatureCard = styled(Card)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  padding: 28px 24px;
  transition:
    transform 500ms var(--ease-out),
    box-shadow 500ms var(--ease-out),
    border-color 200ms ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: radial-gradient(360px circle at var(--mx, 50%) var(--my, 0%), var(--spotlight), transparent 55%);
    opacity: 0;
    transition: opacity 400ms ease;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-md);
      border-color: var(--separator-strong);
    }

    &:hover::before {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }

  p {
    font-size: 0.9375rem;
    line-height: 1.5;
    color: var(--text-secondary);
  }
`;

const IconTile = styled.span`
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  margin-bottom: 10px;
  border-radius: 11px;
  background: var(--accent-tint);
  color: var(--accent);
`;

const trackPointer = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`);
  event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`);
};

function Landing({ locale }) {
  const { hero, stats, coverage, featuresHeader, features, workspace, home, market } = locale;
  const glowRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  const moveGlow = (event) => {
    const glow = glowRef.current;
    if (!glow || reduced) return;
    const rect = event.currentTarget.getBoundingClientRect();
    glow.style.opacity = '1';
    glow.style.transform = `translate3d(${event.clientX - rect.left}px, ${event.clientY - rect.top}px, 0)`;
  };

  const hideGlow = () => {
    if (glowRef.current) glowRef.current.style.opacity = '0';
  };

  return (
    <>
      <HeroSection onPointerMove={moveGlow} onPointerLeave={hideGlow}>
        <PointerGlow ref={glowRef} aria-hidden="true" />
        <Container>
          <HeroInner>
            <Rise $delay={0}>
              <LiveChip>
                <LiveDot />
                {hero.tag}
              </LiveChip>
            </Rise>
            <DisplayTitle>
              <RiseWords text={hero.title} delay={120} step={70} />
            </DisplayTitle>
            <Rise $delay={420}>
              <Lead>{hero.text}</Lead>
            </Rise>
            <HeroActions $delay={560}>
              <Button as="a" href="#workspace">
                {hero.primaryAction}
              </Button>
              <TextLink as={Link} to="/about">
                {hero.secondaryAction}
                <IconChevronRight size={16} strokeWidth={2} />
              </TextLink>
            </HeroActions>
          </HeroInner>

          <PulseGraphic />
          <HeroStats stats={stats} />
        </Container>
      </HeroSection>

      <CoverageSection aria-label={coverage.label}>
        <CoverageLabel>{coverage.label}</CoverageLabel>
        <Ticker>
          <TickerTrack>
            <ul>
              {activeExchanges.map((exchange) => (
                <li key={exchange.id}>{exchange.name}</li>
              ))}
            </ul>
            <ul aria-hidden="true">
              {activeExchanges.map((exchange) => (
                <li key={exchange.id}>{exchange.name}</li>
              ))}
            </ul>
          </TickerTrack>
        </Ticker>
      </CoverageSection>

      <MarketSnapshot locale={market} />

      <Section>
        <Container>
          <SectionHeader>
            <Eyebrow>{featuresHeader.eyebrow}</Eyebrow>
            <SectionTitle>{featuresHeader.title}</SectionTitle>
          </SectionHeader>
          <FeatureGrid>
            {features.map((feature, index) => {
              const Icon = featureIcons[index % featureIcons.length];
              return (
                <Reveal key={feature.title} delay={index * 90}>
                  <FeatureCard onPointerMove={trackPointer}>
                    <IconTile>
                      <Icon size={20} />
                    </IconTile>
                    <CardTitle>{feature.title}</CardTitle>
                    <p>{feature.text}</p>
                  </FeatureCard>
                </Reveal>
              );
            })}
          </FeatureGrid>
        </Container>
      </Section>

      <Section id="workspace">
        <Container>
          <SectionHeader>
            <Eyebrow>{workspace.eyebrow}</Eyebrow>
            <SectionTitle>{workspace.title}</SectionTitle>
            <Lead>{workspace.lead}</Lead>
          </SectionHeader>
          <Reveal>
            <Home locale={home} />
          </Reveal>
        </Container>
      </Section>
    </>
  );
}

export default Landing;
